import type { FieldValueResult } from '@/components/documents/Foxit/FormMapping/composables/FieldValueProvider'
import type { MappedCartItem } from '@/components/documents/Foxit/FormMapping/composables/FormFillHelper'
import { useFormFillHelper } from '@/components/documents/Foxit/FormMapping/composables/FormFillHelper'
import { PbProductType } from '@/enums/PbProductType'
import { PbSubcategory } from '@/enums/PbSubcategory'

export const useMerchandiseFields = () => {
	const {
		findCartItems,
		getFieldDescription,
		getFieldPrice,
		findCartItemsInMultipleSubcategories,
		getAttributeValue,
		generateOtherLineFieldNames,
		multilineOtherFields,
		otherLineCounts,
		multilineWithSingleTotalFields,
		sectionOverrides,
		categoryOverrides,
		findCartItemsInMultipleCategories,
	} = useFormFillHelper()

	const casketManufacturerDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Merchandise, PbSubcategory.MerchandiseCasket)

		return [
			{
				name: fieldName,
				value: getAttributeValue(items, 'productManufacturer'),
				format: false,
				section: 'merchandise',
			},
		]
	}

	const casketModelNumber = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Merchandise, PbSubcategory.MerchandiseCasket)

		return [
			{
				name: fieldName,
				value: getAttributeValue(items, 'modelNameNumber'),
				format: false,
				section: 'merchandise',
			},
		]
	}

	const casketInteriorDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Merchandise, PbSubcategory.MerchandiseCasket)

		return [
			{
				name: fieldName,
				value: getAttributeValue(items, 'interiorDescription'),
				format: false,
				section: 'merchandise',
			},
		]
	}

	const casketExteriorDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Merchandise, PbSubcategory.MerchandiseCasket)

		return [
			{
				name: fieldName,
				value: getAttributeValue(items, 'exteriorDescription'),
				format: false,
				section: 'merchandise',
			},
		]
	}

	const casketPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Merchandise, PbSubcategory.MerchandiseCasket)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'merchandise',
				supplementalItems: items,
			},
		]
	}

	const casketFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Merchandise,
			PbSubcategory.MerchandiseCasket
		)

		return [
			...casketManufacturerDesc(fieldNames.manufacturerDesc, items),
			...casketModelNumber(fieldNames.modelNameNumber, items),
			...casketInteriorDesc(fieldNames.interiorDesc, items),
			...casketExteriorDesc(fieldNames.exteriorDesc, items),
			...casketPrice(fieldNames.price, items),
		]
	}

	const merchandiseOtherDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Merchandise, PbSubcategory.Other)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'merchandise',
			},
		]
	}

	const merchandiseOtherPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Merchandise, PbSubcategory.Other)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'merchandise',
				supplementalItems: items,
			},
		]
	}

	const merchandiseOtherFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(PbProductType.Merchandise, PbSubcategory.Other)

		return [
			...merchandiseOtherDesc(fieldNames.description, items),
			...merchandiseOtherPrice(fieldNames.price, items),
		]
	}

	const merchandiseOther2Desc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Merchandise, PbSubcategory.Other)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'merchandise',
			},
		]
	}

	const merchandiseOther2Price = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Merchandise, PbSubcategory.Other)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'merchandise',
				supplementalItems: items,
			},
		]
	}

	const merchandiseOther2Fields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(PbProductType.Merchandise, PbSubcategory.Other2)

		return [
			...merchandiseOther2Desc(fieldNames.description, items),
			...merchandiseOther2Price(fieldNames.price, items),
		]
	}

	const merchandiseOtherAndDiscountDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItemsInMultipleSubcategories(PbProductType.Merchandise, [
				PbSubcategory.Other,
				PbSubcategory.Discount,
			])

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'merchandise',
			},
		]
	}

	const merchandiseOtherAndDiscountPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItemsInMultipleSubcategories(PbProductType.Merchandise, [
				PbSubcategory.Other,
				PbSubcategory.Discount,
			])

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'merchandise',
				supplementalItems: items,
			},
		]
	}

	const merchandiseOtherAndDiscountFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItemsInMultipleSubcategories(
			PbProductType.Merchandise,
			[PbSubcategory.Other, PbSubcategory.Discount]
		)

		return [
			...merchandiseOtherAndDiscountDesc(fieldNames.description, items),
			...merchandiseOtherAndDiscountPrice(fieldNames.price, items),
		]
	}

	const merchandiseOtherAndDiscountMultilineFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItemsInMultipleSubcategories(
			PbProductType.Merchandise,
			[PbSubcategory.Other, PbSubcategory.Discount]
		)

		return multilineWithSingleTotalFields(items, fieldNames, 'merchandise')
	}

	const multilineMerchandiseOtherFields = (
		fieldNames: string
	): FieldValueResult[] => {
		if (!otherLineCounts.merchandise) {
			console.error('Merchandise other line count not set')

			return []
		}

		const items = [
			...findCartItems(PbProductType.Merchandise, PbSubcategory.Discount),
			...findCartItems(PbProductType.Merchandise, PbSubcategory.Other),
		]

		const fields = generateOtherLineFieldNames(
			fieldNames,
			otherLineCounts.merchandise
		)

		return multilineOtherFields(fields, items, 'merchandise')
	}

	const acknowledgeCardsDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItemsInMultipleCategories(
				categoryOverrides.find((override) => override.field === fieldName)
					?.categories ?? [PbProductType.Merchandise],
				PbSubcategory.MerchandiseAckCards
			)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section:
					sectionOverrides.find((override) => override.field === fieldName)
						?.section ?? 'merchandise',
			},
		]
	}

	const acknowledgeCardsPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItemsInMultipleCategories(
				categoryOverrides.find((override) => override.field === fieldName)
					?.categories ?? [PbProductType.Merchandise],
				PbSubcategory.MerchandiseAckCards
			)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section:
					sectionOverrides.find((override) => override.field === fieldName)
						?.section ?? 'merchandise',
				supplementalItems: items,
			},
		]
	}

	const acknowledgeCardsFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItemsInMultipleCategories(
			categoryOverrides.find(
				(override) => override.field === fieldNames.description
			)?.categories ?? [PbProductType.Merchandise],
			PbSubcategory.MerchandiseAckCards
		)

		return [
			...acknowledgeCardsDesc(fieldNames.description, items),
			...acknowledgeCardsPrice(fieldNames.price, items),
		]
	}

	const memorialFoldersDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItemsInMultipleCategories(
				categoryOverrides.find((override) => override.field === fieldName)
					?.categories ?? [PbProductType.Merchandise],
				PbSubcategory.MerchandiseMemorialFolders
			)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section:
					sectionOverrides.find((override) => override.field === fieldName)
						?.section ?? 'merchandise',
			},
		]
	}

	const memorialFoldersPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItemsInMultipleCategories(
				categoryOverrides.find((override) => override.field === fieldName)
					?.categories ?? [PbProductType.Merchandise],
				PbSubcategory.MerchandiseMemorialFolders
			)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section:
					sectionOverrides.find((override) => override.field === fieldName)
						?.section ?? 'merchandise',
				supplementalItems: items,
			},
		]
	}

	const memorialFoldersFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItemsInMultipleCategories(
			categoryOverrides.find(
				(override) => override.field === fieldNames.description
			)?.categories ?? [PbProductType.Merchandise],
			PbSubcategory.MerchandiseMemorialFolders
		)

		return [
			...memorialFoldersDesc(fieldNames.description, items),
			...memorialFoldersPrice(fieldNames.price, items),
		]
	}

	const memorialBookDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItemsInMultipleCategories(
				categoryOverrides.find((override) => override.field === fieldName)
					?.categories ?? [PbProductType.Merchandise],
				PbSubcategory.MerchandiseMemorialBook
			)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section:
					sectionOverrides.find((override) => override.field === fieldName)
						?.section ?? 'merchandise',
			},
		]
	}

	const memorialBookPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItemsInMultipleCategories(
				categoryOverrides.find((override) => override.field === fieldName)
					?.categories ?? [PbProductType.Merchandise],
				PbSubcategory.MerchandiseMemorialBook
			)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section:
					sectionOverrides.find((override) => override.field === fieldName)
						?.section ?? 'merchandise',
				supplementalItems: items,
			},
		]
	}

	const memorialBookFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItemsInMultipleCategories(
			categoryOverrides.find(
				(override) => override.field === fieldNames.description
			)?.categories ?? [PbProductType.Merchandise],
			PbSubcategory.MerchandiseMemorialBook
		)

		return [
			...memorialBookDesc(fieldNames.description, items),
			...memorialBookPrice(fieldNames.price, items),
		]
	}

	const outerBurialContainerManufacturerDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Merchandise,
				PbSubcategory.MerchandiseOuterBurialContainer
			)

		return [
			{
				name: fieldName,
				value: getAttributeValue(items, 'productManufacturer'),
				format: false,
				section: 'merchandise',
			},
		]
	}

	const outerBurialContainerModelDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Merchandise,
				PbSubcategory.MerchandiseOuterBurialContainer
			)

		return [
			{
				name: fieldName,
				value: getAttributeValue(items, 'modelNameNumber'),
				format: false,
				section: 'merchandise',
			},
		]
	}

	const outerBurialConstructedOfDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Merchandise,
				PbSubcategory.MerchandiseOuterBurialContainer
			)

		return [
			{
				name: fieldName,
				value: getAttributeValue(items, 'exteriorDescription'),
				format: false,
				section: 'merchandise',
			},
		]
	}

	const outerBurialPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Merchandise,
				PbSubcategory.MerchandiseOuterBurialContainer
			)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'merchandise',
				supplementalItems: items,
			},
		]
	}

	const outerBurialContainerDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Merchandise,
				PbSubcategory.MerchandiseOuterBurialContainer
			)

		return [
			{
				name: fieldName,
				value: getAttributeValue(items, 'shortDescription'),
				format: false,
				section: 'merchandise',
			},
		]
	}

	const outerBurialFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Merchandise,
			PbSubcategory.MerchandiseOuterBurialContainer
		)
		return [
			...outerBurialContainerManufacturerDesc(
				fieldNames.manufacturerDesc,
				items
			),
			...outerBurialContainerModelDesc(fieldNames.modelDesc, items),
			...outerBurialConstructedOfDesc(fieldNames.constructedOfDesc, items),
			...outerBurialPrice(fieldNames.price, items),
			...outerBurialContainerDesc(fieldNames.shortDescription, items),
		]
	}

	const urnManufacturerDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Merchandise, PbSubcategory.MerchandiseUrn)

		return [
			{
				name: fieldName,
				value: getAttributeValue(items, 'productManufacturer'),
				format: false,
				section: 'merchandise',
			},
		]
	}

	const urnModelDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Merchandise, PbSubcategory.MerchandiseUrn)

		return [
			{
				name: fieldName,
				value: getAttributeValue(items, 'modelNameNumber'),
				format: false,
				section: 'merchandise',
			},
		]
	}

	const urnConstructedOfDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Merchandise, PbSubcategory.MerchandiseUrn)

		return [
			{
				name: fieldName,
				value: getAttributeValue(items, 'exteriorDescription'),
				format: false,
				section: 'merchandise',
			},
		]
	}

	const urnPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Merchandise, PbSubcategory.MerchandiseUrn)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'merchandise',
				supplementalItems: items,
			},
		]
	}

	const urnFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Merchandise,
			PbSubcategory.MerchandiseUrn
		)
		return [
			...urnManufacturerDesc(fieldNames.manufacturerDesc, items),
			...urnModelDesc(fieldNames.modelDesc, items),
			...urnConstructedOfDesc(fieldNames.constructedOfDesc, items),
			...urnPrice(fieldNames.price, items),
		]
	}

	const cremationAltContainerDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Merchandise,
			PbSubcategory.MerchandiseAlternativeContainer
		)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'merchandise',
			},
		]
	}

	const cremationAltContainerPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Merchandise,
			PbSubcategory.MerchandiseAlternativeContainer
		)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'merchandise',
				supplementalItems: items,
			},
		]
	}

	const cremationAltContainerFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Merchandise,
			PbSubcategory.MerchandiseAlternativeContainer
		)

		return [
			...cremationAltContainerDesc(fieldNames.description, items),
			...cremationAltContainerPrice(fieldNames.price, items),
		]
	}

	return {
		casketManufacturerDesc,
		casketModelNumber,
		casketInteriorDesc,
		casketExteriorDesc,
		casketPrice,
		casketFields,
		merchandiseOtherDesc,
		merchandiseOtherPrice,
		merchandiseOtherFields,
		merchandiseOther2Desc,
		merchandiseOther2Price,
		merchandiseOther2Fields,
		merchandiseOtherAndDiscountDesc,
		merchandiseOtherAndDiscountPrice,
		merchandiseOtherAndDiscountFields,
		multilineMerchandiseOtherFields,
		merchandiseOtherAndDiscountMultilineFields,
		acknowledgeCardsDesc,
		acknowledgeCardsPrice,
		acknowledgeCardsFields,
		memorialFoldersDesc,
		memorialFoldersPrice,
		memorialFoldersFields,
		memorialBookDesc,
		memorialBookPrice,
		memorialBookFields,
		outerBurialContainerManufacturerDesc,
		outerBurialContainerModelDesc,
		outerBurialConstructedOfDesc,
		outerBurialPrice,
		outerBurialFields,
		urnManufacturerDesc,
		urnModelDesc,
		urnConstructedOfDesc,
		urnPrice,
		urnFields,
		cremationAltContainerDesc,
		cremationAltContainerPrice,
		cremationAltContainerFields,
	}
}
